import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format } from 'date-fns';
import { Icon } from '../../../../components';
import { getChangeHistory } from '../../../../services';
import { notifyError } from '../../../../utils';
import * as s from './styled-machine-change-log';

const TYPE_LABEL = {
  setpoint: 'Setpoint',
  command: 'Comando',
  range: 'Range',
};

const TYPE_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'setpoint', label: 'Setpoints' },
  { key: 'command', label: 'Comandos' },
  { key: 'range', label: 'Ranges' },
];

const getFirst = (item, fields) => {
  for (let i = 0; i < fields.length; i += 1) {
    const value = fields[i]
      .split('.')
      .reduce((acc, key) => acc && acc[key], item);
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd/MM/yyyy HH:mm');
};

const stringifyValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (typeof value !== 'string') {
    return JSON.stringify(value);
  }

  const present = (item) => item !== null && item !== undefined && item !== '';

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const text = parsed
        .map((item) => {
          const limits = [item.min, item.max].filter(present).join(' - ');
          return [item.category, item.level, limits, item.unit]
            .filter(present)
            .join(' ');
        })
        .filter(present)
        .join(' | ');
      return text || '—';
    }
    return JSON.stringify(parsed);
  } catch {
    return value;
  }
};

const normalizeThingText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const itemMatchesThing = (item, thing) => {
  if (!thing) return true;

  const itemThingId = getFirst(item, [
    'thingId',
    'thing.id',
    'equipmentId',
    'machineId',
  ]);
  const itemThingName = getFirst(item, [
    'thingName',
    'thing.name',
    'thing.thing',
    'equipmentName',
    'machineName',
  ]);

  const hasMachineIdentity = itemThingId != null || itemThingName != null;
  if (!hasMachineIdentity) return true;

  const thingId = thing.id != null ? String(thing.id) : null;
  const thingNames = [thing.thing, thing.name]
    .map(normalizeThingText)
    .filter(Boolean);

  if (thingId && itemThingId != null && String(itemThingId) === thingId) {
    return true;
  }

  return (
    itemThingName != null &&
    thingNames.includes(normalizeThingText(itemThingName))
  );
};

const normalizeRow = (item, index) => {
  const type = getFirst(item, ['type', 'changeType', 'entityType']) || 'range';
  const changedAt = getFirst(item, [
    'changedAt',
    'createdAt',
    'timestamp',
    'insertdate',
  ]);

  return {
    id: getFirst(item, ['id', 'auditId']) || `${type}-${index}`,
    type,
    typeLabel: TYPE_LABEL[type] || type,
    itemName:
      getFirst(item, [
        'itemName',
        'setpointName',
        'commandName',
        'variableName',
        'variable.name',
        'setpoint.label',
        'command.name',
      ]) || 'Item alterado',
    previousValueLabel: stringifyValue(
      getFirst(item, ['previousValue', 'oldValue', 'before'])
    ),
    currentValueLabel: stringifyValue(
      getFirst(item, ['currentValue', 'newValue', 'after'])
    ),
    userEmail:
      getFirst(item, [
        'userName',
        'email',
        'user.email',
        'username',
        'userDisplayName',
        'user.name',
      ]) || 'Usuário não identificado',
    changedAtLabel: formatDateTime(changedAt),
    changedAt,
  };
};

const MachineChangeLog = ({ thing, parkId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('all');
  const fetchTokenRef = useRef(0);

  const loadLogs = useCallback(() => {
    if (!thing || !thing.id) {
      setItems([]);
      setLoading(false);
      return;
    }

    fetchTokenRef.current += 1;
    const token = fetchTokenRef.current;
    setLoading(true);

    getChangeHistory(1, 50, {
      thingId: thing.id,
      parkId,
    })
      .then(({ data }) => {
        if (token !== fetchTokenRef.current) return;
        const rawItems = Array.isArray(data?.items) ? data.items : [];
        setItems(rawItems.filter((item) => itemMatchesThing(item, thing)));
      })
      .catch((error) => {
        if (token !== fetchTokenRef.current) return;
        setItems([]);
        notifyError(
          error?.message || 'Ocorreu um erro ao carregar os logs da máquina'
        );
      })
      .finally(() => {
        if (token === fetchTokenRef.current) setLoading(false);
      });
  }, [thing, parkId]);

  useEffect(() => {
    setType('all');
    loadLogs();
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [loadLogs]);

  const rows = useMemo(
    () =>
      items
        .map(normalizeRow)
        .sort(
          (a, b) =>
            new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
        ),
    [items]
  );

  const counts = useMemo(() => {
    const result = { all: rows.length, setpoint: 0, command: 0, range: 0 };
    rows.forEach((row) => {
      if (result[row.type] != null) result[row.type] += 1;
    });
    return result;
  }, [rows]);

  const visibleRows = useMemo(() => {
    const filtered =
      type === 'all' ? rows : rows.filter((row) => row.type === type);
    return filtered.slice(0, 10);
  }, [rows, type]);

  return (
    <s.Container>
      <s.Header>
        <s.HeaderText>
          <s.Chip>Alterações</s.Chip>
          <s.Hint>Setpoints, comandos e ranges da máquina</s.Hint>
        </s.HeaderText>
        <s.RefreshButton type="button" onClick={loadLogs} disabled={loading}>
          <Icon name="refresh" size={14} />
          Atualizar
        </s.RefreshButton>
      </s.Header>

      <s.FilterRow>
        {TYPE_FILTERS.map((filter) => {
          const count = counts[filter.key] || 0;
          const disabled = filter.key !== 'all' && count === 0;
          return (
            <s.FilterChip
              key={filter.key}
              type="button"
              $active={type === filter.key}
              disabled={disabled}
              onClick={() => !disabled && setType(filter.key)}
            >
              {filter.label}
              <span className="n">{count}</span>
            </s.FilterChip>
          );
        })}
      </s.FilterRow>

      {loading ? (
        <s.LogList>
          {Array.from({ length: 3 }, (_, index) => (
            <s.LogItem key={`machine-change-log-loading-${index}`}>
              <s.Skeleton $w="72px" />
              <s.Skeleton $w="82%" />
              <s.Skeleton $w="60px" />
              <s.Skeleton $w="60px" />
              <s.Skeleton $w="98px" />
            </s.LogItem>
          ))}
        </s.LogList>
      ) : visibleRows.length ? (
        <s.LogList>
          {visibleRows.map((row) => (
            <s.LogItem key={`${row.type}-${row.id}`}>
              <s.TypeBadge $type={row.type}>{row.typeLabel}</s.TypeBadge>
              <s.MainCell>
                <s.ItemName title={row.itemName}>{row.itemName}</s.ItemName>
                <s.UserEmail title={row.userEmail}>{row.userEmail}</s.UserEmail>
              </s.MainCell>
              <s.ValueCell $tone="previous" title={row.previousValueLabel}>
                {row.previousValueLabel}
              </s.ValueCell>
              <s.ValueCell $tone="current" title={row.currentValueLabel}>
                {row.currentValueLabel}
              </s.ValueCell>
              <s.DateCell>{row.changedAtLabel}</s.DateCell>
            </s.LogItem>
          ))}
        </s.LogList>
      ) : (
        <s.State>
          <Icon name="history" size={18} />
          Nenhuma alteração de setpoint, comando ou range para esta máquina.
        </s.State>
      )}
    </s.Container>
  );
};

export default MachineChangeLog;
